/**
 * UI data reader for Ory flows.
 *
 * Provides static methods to read data from flow UI structures (forms, fields, messages).
 * Use this when rendering components or accessing form data.
 *
 * For API response analysis (AAL, redirects), see OryResponseReader.
 *
 * @see OryResponseReader - For reading data from API execution responses
 */

import type { OryKratosFlowWithUI } from '../model';
import { isOryInputNode, isOryCsrfTokenNode, isOryAnchorNode } from './guards';
import type { UiText } from '@ory/client';

export type OryMessageType = 'error' | 'info' | 'success';

export type OryMessageScope = 'global' | 'fields' | 'all';

/**
 * UI Node Group types from Ory Kratos.
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface
 */
export type OryNodeGroup =
  | 'default'           // CSRF token, general fields
  | 'password'          // Password authentication
  | 'oidc'              // Social login (Google, GitHub, etc.)
  | 'profile'           // Profile management
  | 'link'              // Account linking
  | 'code'              // Email/SMS codes
  | 'totp'              // 2FA apps
  | 'lookup_secret'     // Backup recovery codes
  | 'webauthn'          // Hardware keys, platform authenticators
  | 'passkey'           // Passkeys (FIDO2)
  | 'identifier_first'  // Identifier-first flow
  | 'captcha'           // CAPTCHA challenges
  | 'saml'              // SAML authentication
  | 'oauth2_consent';   // OAuth2 consent screens

/**
 * Options for filtering messages
 */
export interface OryMessageOptions {
  /** Filter by message type. Default: all types */
  types?: OryMessageType[];

  /** Where to extract messages from. Default: 'all' */
  scope?: OryMessageScope;

  /** Filter by specific fields (input names or node IDs). Only applies when scope includes 'fields' */
  fields?: string[];
}

export class OryFlowReader {

  /**
   * Get CSRF token required for form submissions
   * @throws Error if CSRF token not found
   */
  static csrfToken(flow: OryKratosFlowWithUI): string {
    const csrfNode = OryFlowReader.#nodes(flow).find(isOryCsrfTokenNode);
    if (!csrfNode) {
      throw new Error('CSRF token not found in flow');
    }
    return String(csrfNode.attributes.value ?? '');
  }

  /**
   * Check if a node exists by name (inputs) or id (img/text)
   */
  static hasField(
    flow: OryKratosFlowWithUI,
    nameOrId: string
  ): boolean {
    return OryFlowReader.#nodes(flow).some(n => {
      if (isOryInputNode(n) && n.attributes.name === nameOrId) {
        return true;
      }
      return 'id' in n.attributes && n.attributes.id === nameOrId;

    });
  }

  /**
   * Get full node by name (inputs) or id (img/text)
   * Use with type guards for full attribute access
   */
  static getNode(
    flow: OryKratosFlowWithUI,
    nameOrId: string
  ) {
    return OryFlowReader.#nodes(flow).find(n => {
      if (isOryInputNode(n) && n.attributes.name === nameOrId) {
        return true;
      }
      return 'id' in n.attributes && n.attributes.id === nameOrId;
    });
  }

  /**
   * Get node value by name (inputs) or id (img/text)
   * - Input nodes: returns `attributes.value`
   * - Image nodes: returns `attributes.src`
   * - Text nodes: returns `attributes.text.text`
   *
   * For additional attributes (e.g., img width/height), use `getNode()` with type guards
   * @returns Value or undefined if node not found
   */
  static fieldValue<T = string>(
    flow: OryKratosFlowWithUI,
    nameOrId: string
  ): T | undefined {
    const node = this.getNode(flow, nameOrId);

    if (!node) {
      return undefined;
    }

    if (isOryInputNode(node)) {
      const value = node.attributes.value;
      return (value !== null && value !== undefined ? value : undefined) as T | undefined;
    }

    if ('src' in node.attributes) {
      return node.attributes.src as T;
    }

    if ('text' in node.attributes && typeof node.attributes.text === 'object' && node.attributes.text !== null && 'text' in node.attributes.text) {
      return node.attributes.text.text as T;
    }

    return undefined;
  }

  /**
   * Check if group exists in flow
   */
  static hasGroup(
    flow: OryKratosFlowWithUI,
    groupName: OryNodeGroup
  ): boolean {
    return OryFlowReader.#nodes(flow).some(node => node.group === groupName);
  }

  /**
   * Get all nodes in a group
   * @returns Empty array if group doesn't exist
   */
  static getGroup(
    flow: OryKratosFlowWithUI,
    groupName: OryNodeGroup
  ) {
    return OryFlowReader.#nodes(flow).filter(node => node.group === groupName);
  }

  /**
   * Get messages from flow (global and/or node-level)
   * Processes ALL node types (input, img, text, etc.)
   */
  static getMessages(
    flow: OryKratosFlowWithUI,
    options: OryMessageOptions = {}
  ): UiText[] {
    const {
      types,
      scope = 'all',
      fields
    } = options;

    const messages: UiText[] = [];

    if (scope === 'global' || scope === 'all') {
      const globalMessages = flow.ui?.messages ?? [];
      const filtered = types
        ? globalMessages.filter(m => types.includes(m.type as OryMessageType))
        : globalMessages;
      messages.push(...filtered);
    }

    if (scope === 'fields' || scope === 'all') {
      const allNodes = OryFlowReader.#nodes(flow);
      const nodesToProcess = fields && fields.length > 0
        ? allNodes.filter(n => {
            // Check input nodes by name
            if (isOryInputNode(n) && fields.includes(n.attributes.name)) {
              return true;
            }
            // Check all nodes by id
            return 'id' in n.attributes && fields.includes(n.attributes.id as string);

          })
        : allNodes;

      for (const node of nodesToProcess) {
        const nodeMessages = types
          ? (node.messages ?? []).filter(m => types.includes(m.type as OryMessageType))
          : (node.messages ?? []);

        messages.push(...nodeMessages);
      }
    }

    return messages;
  }

  /**
   * Get messages for a specific field (input name or node id)
   */
  static getMessagesFor(
    flow: OryKratosFlowWithUI,
    fieldName: string,
    options: Omit<OryMessageOptions, 'scope' | 'fields'> = {}
  ): UiText[] {
    return this.getMessages(flow, {
      ...options,
      scope: 'fields',
      fields: [fieldName]
    });
  }

  /**
   * Check if flow has any messages matching options
   */
  static hasMessages(
    flow: OryKratosFlowWithUI,
    options: OryMessageOptions = {}
  ): boolean {
    return this.getMessages(flow, options).length > 0;
  }

  /**
   * Extracts continue URL from flow UI anchor nodes.
   *
   * @remarks
   * When Ory Kratos verification completes with `state: "passed_challenge"`,
   * the redirect URL is NOT in `continue_with` array but in an anchor (`<a>`)
   * node with `id: "continue"` in the UI. This method extracts that URL.
   *
   * This is the Ory-recommended pattern for OAuth2 flows where the user needs
   * to be redirected back to the authorization endpoint after verification.
   *
   * @see https://www.ory.com/docs/kratos/self-service/flows/verification
   *
   * @param flow - Flow object with UI containing potential anchor nodes
   * @returns The href from the 'continue' anchor node, or undefined if not found
   *
   * @example
   * ```typescript
   * // After verification with passed_challenge state
   * const continueUrl = OryFlowReader.getContinueUrl(verificationFlow);
   * // Returns: "http://localhost:5100/oauth2/auth?client_id=..."
   *
   * // No continue anchor (regular flow)
   * const noUrl = OryFlowReader.getContinueUrl(loginFlow);
   * // Returns: undefined
   * ```
   */
  static getContinueUrl(flow: OryKratosFlowWithUI): string | undefined {
    // Find anchor node with id='continue' (Ory's standard for passed_challenge continuation)
    const continueNode = OryFlowReader.#nodes(flow).find(
      node => isOryAnchorNode(node) && node.attributes.id === 'continue'
    );

    if (continueNode && isOryAnchorNode(continueNode)) {
      return continueNode.attributes.href;
    }

    return undefined;
  }

  static #nodes(flow: OryKratosFlowWithUI) {
    return flow.ui?.nodes ?? [];
  }
}
